import { FC, useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import {
	Box,
	CardContent,
	Chip,
	IconButton,
	Pagination,
	Paper,
	Skeleton,
	Stack,
	Typography,
} from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { receiptDownloadUrl } from 'utils/receipt';
import { useDispatch, useSelector } from 'react-redux';
import { getTransactionsIsLoad, getTransactionsList } from 'redux/reducers/transactions/selectors';
import { getTransactionsRequest } from 'redux/reducers/transactions/reducer';
import { TTransactionsItem } from 'redux/reducers/transactions/types';
import { PAGE_SIZE } from 'services/constants/env';
import {
	CARD_BRAND_LABELS,
	LEGACY_RECEIPT_HINT,
	LEGACY_SOURCE_LABEL,
	LEGACY_STATUS_STATES,
	PAYMENT_METHOD_LABELS,
	REFUND_LABEL,
	REFUND_STATUS_LABELS,
	STATE_COLORS,
	STATE_LABELS,
	TRANSACTION_KIND_REFUND,
	TRANSACTION_SOURCE_LEGACY,
	TRANSACTION_TYPE_LABELS,
	TRANSACTIONS_STATE_REFUNDED,
} from 'redux/reducers/transactions/constants';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { EasTooltip } from 'ui/EasTooltip';
import { COLORS } from 'services/constants/colors';

const ROWS = [0, 1, 2, 3, 4];
const GRID_COLUMNS = '1.2fr 4fr 1.6fr 1.6fr 1.1fr';

const stateLabel = (state: string | null) =>
	state ? (STATE_LABELS[state.toUpperCase()] ?? state) : 'Unknown status';

const stateColor = (state: string | null) =>
	(state && STATE_COLORS[state.toUpperCase()]) || '#ffa700';

// Status of the row: rows of the previous payment system by their status, gateway rows by the compat state;
// refund rows as "Refund" (or "Refund pending/failed/cancelled").
const statusView = (item: TTransactionsItem) => {
	const state =
		(item.source === TRANSACTION_SOURCE_LEGACY && LEGACY_STATUS_STATES[item.status]) || item.state;
	if (item.kind === TRANSACTION_KIND_REFUND) {
		const notSucceededLabel = REFUND_STATUS_LABELS[item.status];
		return notSucceededLabel
			? { label: notSucceededLabel, color: stateColor(state) }
			: { label: REFUND_LABEL, color: stateColor(TRANSACTIONS_STATE_REFUNDED) };
	}
	return { label: stateLabel(state), color: stateColor(state) };
};

// Payment method: the label of the gateway ("Visa •••• 4242", "Direct Debit (Barclays)"),
// otherwise the compat fields.
const methodDescription = (item: TTransactionsItem) => {
	if (item.label) return item.label;
	const method = item.paymentMethodType
		? (PAYMENT_METHOD_LABELS[item.paymentMethodType.toUpperCase()] ?? item.paymentMethodType)
		: '';
	const brand = item.cardBrand
		? (CARD_BRAND_LABELS[item.cardBrand.toUpperCase()] ?? item.cardBrand)
		: '';
	const last4 = item.cardLast4 && item.cardLast4 !== '0000' ? `•••• ${item.cardLast4}` : '';
	return [method, brand, last4].filter(Boolean).join(' ');
};

const serviceName = (item: TTransactionsItem) =>
	item.service?.name ?? (item.purpose === 'eu_ddp_prepayment' ? 'EU DDP prepayment' : '—');

export const TransactionHistory: FC = () => {
	const transactions = useSelector(getTransactionsList);
	const transactionsIsLoad = useSelector(getTransactionsIsLoad);
	const dispatch = useDispatch();
	const [currentPage, setCurrentPage] = useState(1);

	useEffect(() => {
		dispatch(getTransactionsRequest({ pageNumber: 1, pageSize: PAGE_SIZE }));
	}, [dispatch]);

	// the page of the loaded data (a payment reloads the first page)
	const shownPage = transactions?.pageNumber || currentPage;

	const handlePageChange = (_event: unknown, page: number) => {
		if (page === shownPage) return;
		setCurrentPage(page);
		dispatch(getTransactionsRequest({ pageNumber: page, pageSize: PAGE_SIZE }));
	};

	const items = transactions?.data ?? [];

	return (
		<Paper
			sx={{
				margin: '10px 10px 20px 10px',
				maxWidth: 'calc(100vw - 20px)',
				overflowX: 'auto',
			}}
		>
			<Box sx={{ width: 'auto', minWidth: '900px' }}>
				<CardContent>
					<Typography
						variant="h5"
						color="primary"
						sx={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '16px' }}
					>
						Transaction history
					</Typography>
					{transactionsIsLoad && (
						<Box sx={{ pb: '1px' }}>
							{ROWS.map((row) => (
								<Skeleton key={row} variant="rectangular" height="30px" sx={{ mb: '30px' }} />
							))}
						</Box>
					)}

					{!transactionsIsLoad && items.length === 0 && (
						<Typography
							variant="h6"
							color="primary"
							sx={{ fontSize: '1.2rem', fontWeight: 'bold', mt: '20px', mb: '16px' }}
						>
							No transactions yet...
						</Typography>
					)}
					{!transactionsIsLoad && items.length > 0 && (
						<>
							<Box
								sx={{
									display: 'grid',
									gridTemplateColumns: GRID_COLUMNS,
									gridGap: '1rem',
									alignItems: 'center',
									mb: 1,
								}}
							>
								<Typography color="primary" sx={{ fontWeight: 'bold' }}>
									Date
								</Typography>
								<Typography color="primary" sx={{ fontWeight: 'bold' }}>
									Description
								</Typography>
								<Typography color="primary" sx={{ fontWeight: 'bold' }}>
									Service
								</Typography>
								<Typography color="primary" sx={{ textAlign: 'right', fontWeight: 'bold' }}>
									Amount
								</Typography>
								<Typography color="primary" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
									Receipt
								</Typography>
							</Box>
							{items.map((item) => {
								const method = methodDescription(item);
								const isLegacy = item.source === TRANSACTION_SOURCE_LEGACY;
								const isRefund = item.kind === TRANSACTION_KIND_REFUND;
								const status = statusView(item);
								// the payment type describes payments, not refunds
								const typeLabel = isRefund ? '' : (TRANSACTION_TYPE_LABELS[item.type] ?? '');
								// rows of the previous payment system and refunds never have a receipt
								const receiptUrl = isLegacy || isRefund ? null : item.receiptUrl;
								return (
									<Box
										key={item.id}
										sx={{
											display: 'grid',
											gridTemplateColumns: GRID_COLUMNS,
											gridGap: '1rem',
											minHeight: '40px',
											alignItems: 'top',
											py: '8px',
										}}
									>
										<div style={{ fontSize: '1rem' }}>
											{item.dateTime
												? DateTime.fromISO(item.dateTime).toLocaleString(DateTime.DATETIME_SHORT)
												: '—'}
										</div>
										<div>
											<b style={{ color: status.color }}>{status.label}</b>
											{method && <span>: {method}</span>}
											{item.invoiceNumber && <span>, invoice number: {item.invoiceNumber}</span>}
											{typeLabel && (
												<Typography
													component="span"
													sx={{ fontSize: '.75rem', color: 'text.secondary' }}
												>
													{' '}
													({typeLabel})
												</Typography>
											)}
											{isLegacy && (
												<EasTooltip title={LEGACY_RECEIPT_HINT}>
													<Chip
														size="small"
														variant="outlined"
														label={LEGACY_SOURCE_LABEL}
														sx={{
															ml: 1,
															height: '18px',
															fontSize: '.7rem',
															color: 'text.secondary',
															verticalAlign: 'middle',
														}}
													/>
												</EasTooltip>
											)}
											{item.comment && (
												<Typography
													component="div"
													sx={{ fontSize: '.8rem', color: 'text.secondary' }}
												>
													{item.comment}
												</Typography>
											)}
										</div>
										<div style={{ fontSize: '1rem' }}>{serviceName(item)}</div>
										<Box
											sx={{
												textAlign: 'right',
												display: 'flex',
												flexDirection: 'column',
												alignItems: 'flex-end',
												// tighter spacing between amount rows
												'& .MuiTypography-root': { lineHeight: 1 },
											}}
										>
											{/* refunds: the returned amount (positive in the response) with a minus sign */}
											<AmountAndCurrency
												amount={isRefund ? -(item.amount ?? 0) : (item.amount ?? 0)}
												currency={item.currency || 'EUR'}
											/>
											{!isRefund && item.fee > 0 && (
												<Box sx={{ m: 0, p: 0, lineHeight: 1 }}>
													<Typography
														color="text.secondary"
														style={{ fontSize: '.7rem' }}
														component="span"
														sx={{ m: 0, p: 0 }}
													>
														incl. bank fee
													</Typography>{' '}
													<AmountAndCurrency
														amount={item.fee}
														currency="EUR"
														size={0.7}
														color="text.secondary"
													/>
												</Box>
											)}
											{item.refundedAmount > 0 && (
												<Box sx={{ m: 0, p: 0, lineHeight: 1 }}>
													<Typography
														color="text.secondary"
														style={{ fontSize: '.7rem' }}
														component="span"
														sx={{ m: 0, p: 0 }}
													>
														refunded{' '}
													</Typography>
													<AmountAndCurrency
														amount={item.refundedAmount}
														currency={item.currency || 'EUR'}
														size={0.7}
														color="text.secondary"
													/>
												</Box>
											)}
										</Box>
										<div style={{ textAlign: 'center' }}>
											{receiptUrl ? (
												<EasTooltip title="Download receipt (PDF)">
													<IconButton
														aria-label="Download receipt (PDF)"
														onClick={() => window.open(receiptDownloadUrl(receiptUrl), '_blank')}
														color="primary"
													>
														<ReceiptLongOutlinedIcon />
													</IconButton>
												</EasTooltip>
											) : isLegacy ? (
												<EasTooltip title={LEGACY_RECEIPT_HINT}>
													<Typography
														component="span"
														sx={{ fontSize: '1rem', color: COLORS.PRIMARY, fontWeight: 'bold' }}
													>
														—
													</Typography>
												</EasTooltip>
											) : (
												<Typography
													component="span"
													sx={{ fontSize: '1rem', color: COLORS.PRIMARY, fontWeight: 'bold' }}
												>
													—
												</Typography>
											)}
										</div>
									</Box>
								);
							})}
						</>
					)}
				</CardContent>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						padding: '10px',
						alignItems: 'center',
					}}
				>
					{(transactions?.totalCount ?? 0) > PAGE_SIZE && (
						<Stack spacing={2} marginTop={2}>
							<Pagination
								count={Math.ceil((transactions?.totalCount ?? 0) / PAGE_SIZE)}
								page={shownPage}
								onChange={handlePageChange}
								shape="rounded"
								color="primary"
								sx={{
									mb: '20px',
									'& .MuiPaginationItem-root': { borderRadius: '12px' },
								}}
							/>
						</Stack>
					)}
					{!!transactions?.totalCount && (
						<Box
							sx={{
								pt: '20px',
								display: 'flex',
								flexDirection: 'row',
							}}
						>
							<Typography
								sx={{
									fontSize: '1rem',
									color: '#555',
								}}
							>
								{transactions?.totalCount ? `Total transactions: ` : ''}
							</Typography>
							&nbsp;
							<Typography
								sx={{
									fontSize: '1rem',
									fontWeight: 'bold',
									color: 'primary.main',
								}}
							>
								{transactions?.totalCount
									? `${String(transactions?.totalCount ?? '')} `
									: 'No data found'}
							</Typography>
						</Box>
					)}
				</Box>
			</Box>
		</Paper>
	);
};
